'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var oidcClient = require('oidc-client');

class TiaAuthClient {
    constructor(params) {
        this.beforeLogin = () => undefined;
        this.afterLogin = () => undefined;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.handleTokenRenew = (userData) => undefined;
        this.afterLogout = () => undefined;
        this.onSignInListeners = [];
        this.onAfterSignInListeners = [];
        this.onTokenRenewListeners = [];
        this.onLogoutListeners = [];
        this.webStorageName = '';
        const authOptions = params.authOptions;
        const accessTokenExpiringNotificationTime = 10;
        this.webStorageName = `tia_auth_${authOptions.webStorageStateName ? authOptions.webStorageStateName : ''}`;
        this.userStore = new oidcClient.WebStorageStateStore({
            prefix: this.webStorageName,
        });
        this.getSignInSilentParams = params.getSignInSilentParams || (() => ({}));
        this.beforeLogin = params.onSignIn || this.beforeLogin;
        this.afterLogin = params.onAfterSignIn || this.afterLogin;
        this.afterLogout = params.onLogout || this.afterLogout;
        this.handleTokenRenew = params.onTokenRenew || this.handleTokenRenew;
        const settings = {
            authority: authOptions.domain,
            userStore: this.userStore,
            client_id: authOptions.clientId,
            redirect_uri: authOptions.redirectUri,
            response_type: 'code',
            scope: authOptions.scope,
            post_logout_redirect_uri: authOptions.successfulLogoutUri,
            filterProtocolClaims: true,
            loadUserInfo: true,
            silentRequestTimeout: 10000,
            accessTokenExpiringNotificationTime: accessTokenExpiringNotificationTime,
            extraQueryParams: {
                audience: authOptions.audience,
            },
            metadata: {
                grant_types_supported: ['refresh_token'],
                token_endpoint: `${authOptions.domain}/oauth/token`,
                issuer: `${authOptions.domain}/`,
                authorization_endpoint: `${authOptions.domain}/authorize`,
                userinfo_endpoint: `${authOptions.domain}/userinfo`,
                end_session_endpoint: `${authOptions.domain}/logout`,
                jwks_uri: `${authOptions.domain}/.well-known/jwks.json`,
                revocation_endpoint: `${authOptions.domain}/oauth/revoke`,
            },
        };
        this.settings = settings;
        this.userManager = new oidcClient.UserManager(settings);
        this.userManager.events.addAccessTokenExpiring(() => tslib.__awaiter(this, void 0, void 0, function* () {
            /** Needed for when application is open in multiple tabs */
            navigator.locks.request('refresh_token', (_) => tslib.__awaiter(this, void 0, void 0, function* () {
                /** Renew user session */
                try {
                    const currentUser = yield this.userManager.getUser();
                    if (currentUser && currentUser.expires_in > accessTokenExpiringNotificationTime)
                        return;
                    const key = `user:${this.settings.authority}:${this.settings.client_id}`;
                    const user = yield this.userStore.get(key).then(JSON.parse);
                    const token = yield this.userManager.signinSilent(this.getSignInSilentParams(user));
                    this.onTokenRenewListeners.forEach((fn) => fn(token));
                }
                catch (e) {
                    console.error('Failed to sign in silently, logging the user out');
                    this.logout();
                }
            }));
        }));
    }
    isAuthenticated() {
        return this.userManager.getUser().then((u) => {
            return !!u && !u.expired;
        });
    }
    getUser() {
        return this.userManager.getUser();
    }
    login(args) {
        this.onSignInListeners.forEach((fn) => fn());
        return this.userManager.signinRedirect(args);
    }
    silentLogin(args) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            this.onSignInListeners.forEach((fn) => fn());
            const signInProps = { extraQueryParams: Object.assign({}, this.userManager.settings.extraQueryParams, args) };
            return this.userManager.signinSilent(signInProps);
        });
    }
    callback() {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            try {
                const authData = yield this.userManager.signinCallback();
                this.onAfterSignInListeners.map((fn) => fn());
                this.onTokenRenewListeners.map((fn) => fn(authData));
                return authData;
            }
            catch (err) {
                console.error(err);
                throw err;
            }
        });
    }
    logout(extraQueryParams) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            this.onLogoutListeners.forEach((fn) => fn());
            try {
                yield this.userManager.revokeAccessToken();
                yield this.userManager.removeUser();
                yield this.userManager.signoutRedirect({
                    extraQueryParams: Object.assign({ client_id: this.settings.client_id, returnTo: this.settings.post_logout_redirect_uri }, extraQueryParams),
                });
            }
            catch (e) {
                console.error(e);
            }
        });
    }
    addOnSignInListener(onSignIn) {
        this.onSignInListeners.push(onSignIn);
    }
    removeOnSignInListener(onSignIn) {
        this.onSignInListeners = this.onSignInListeners.filter((fn) => fn !== onSignIn);
    }
    addOnAfterSignInListener(onAfterSignIn) {
        this.onAfterSignInListeners.push(onAfterSignIn);
    }
    removeOnAfterSignInListener(onAfterSignIn) {
        this.onAfterSignInListeners = this.onAfterSignInListeners.filter((fn) => fn !== onAfterSignIn);
    }
    addOnTokenRenewListener(onTokenRenew) {
        this.onTokenRenewListeners.push(onTokenRenew);
    }
    removeOnTokenRenewListener(onTokenRenew) {
        this.onTokenRenewListeners = this.onTokenRenewListeners.filter((fn) => fn !== onTokenRenew);
    }
    addOnLogoutListener(onLogout) {
        this.onLogoutListeners.push(onLogout);
    }
    removeOnLogoutListener(onLogout) {
        this.onLogoutListeners = this.onLogoutListeners.filter((fn) => fn !== onLogout);
    }
    getClientEvents() {
        return this.userManager.events;
    }
}

exports.TiaAuthClient = TiaAuthClient;
